import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, X, ArrowRight,
  LayoutGrid, Newspaper, Users, FileText, Cog, BookOpen, Briefcase, ClipboardList, Lightbulb, Route,
} from 'lucide-react';
import { useT, useLocale } from '../contexts/LocaleProvider';
import { useAppCatalogue, iconFor, LT } from '../contexts/AppCatalogueContext';
import { localizedApp } from '../lib/appLocale';
import { localizedPost } from '../lib/announcementLocale';
import { searchAll, groupByKind } from '../lib/search';
import api from '../utils/api';

/* HR & system pages that don't have an entity list of their own · hand
 * them to the search aggregator as static rows. Add a row here whenever
 * a new top-level page lands. */
function staticPages(t, isAdmin) {
  const rows = [
    { kind: 'page', id: 'expenses',   title: t('nav.expenses'),       subtitle: 'Notes de frais',         route: '/expenses',     icon: 'FileText' },
    { kind: 'page', id: 'directory',  title: t('nav.directory'),      subtitle: 'Annuaire',               route: '/directory',    icon: 'Users' },
    { kind: 'page', id: 'apps',       title: t('apps.title'),         subtitle: 'Catalogue',              route: '/apps',         icon: 'LayoutGrid' },
    { kind: 'page', id: 'resources',  title: t('nav.policies'),       subtitle: 'Ressources RH',          route: '/resources',    icon: 'BookOpen' },
    { kind: 'page', id: 'newsletters',title: t('nav.newsletters'),    subtitle: t('nav.read'),          route: '/resources/newsletters', icon: 'BookOpen' },
    { kind: 'page', id: 'training',   title: t('nav.training'),       subtitle: 'Ressources RH',          route: '/resources/training', icon: 'BookOpen' },
    { kind: 'page', id: 'career-map',  title: t('nav.careerMap'),     subtitle: `${t('nav.career')} · niveaux · niveaus · levels`, route: '/career-map', icon: 'Route' },
    { kind: 'page', id: 'lighting-guide', title: t('nav.lightingGuide'), subtitle: `${t('nav.read')} · Lighting Guide`, route: '/guides/lighting/', icon: 'Lightbulb', external: true },
    { kind: 'page', id: 'ai-survey',  title: t('nav.aiSurvey'),       subtitle: 'Enquête · Bevraging · Survey · IA AI', route: '/surveys/ai-at-work', icon: 'ClipboardList' },
  ];
  if (isAdmin) {
    rows.push({ kind: 'page', id: 'admin', title: t('nav.adminPanel'), subtitle: 'Panneau admin', route: '/admin', icon: 'Cog' });
    rows.push({ kind: 'page', id: 'jobs', title: t('admin.tab.jobs'), subtitle: 'Recrutement · jobs.lamptwist.be', route: '/admin/jobs', icon: 'Briefcase' });
  }
  return rows;
}

const KIND_LABEL = {
  app:      'apps.title',
  post:     'splash.news.title',
  person:   'splash.team.title',
  page:     'search.kind.pages',
};

const STATIC_ICONS = { LayoutGrid, Newspaper, Users, FileText, Cog, BookOpen, Briefcase, ClipboardList, Lightbulb, Route };

export default function SearchPalette({ open, onClose, isAdmin }) {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { apps } = useAppCatalogue();
  const inputRef = useRef(null);
  const [q, setQ] = useState('');
  const [posts, setPosts]       = useState([]);
  const [employees, setEmps]    = useState([]);
  const [activeIdx, setActive]  = useState(0);

  /* Lazy load · only fetch when the palette opens. Cached in component
   * state for the session (cheap enough to refetch on each open). */
  useEffect(() => {
    if (!open) return;
    setActive(0);
    setTimeout(() => inputRef.current?.focus(), 0);
    if (employees.length === 0) {
      api.employees().then(setEmps).catch(() => setEmps([]));
    }
    if (posts.length === 0) {
      api.announcements().then(setPosts).catch(() => setPosts([]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* Sources · one closure per content type. Future feature = one more
   * closure added here. See memory note on searchable-by-default. */
  const sources = useMemo(() => [
    () => apps.filter(a => a.is_active).map(a => {
      const { name, description } = localizedApp(a, locale);
      return {
        kind: 'app',
        id: a.slug,
        title: name,
        subtitle: description || a.group,
        route: a.internal_route || a.href || `/apps/${a.slug}`,
        external: !a.internal_route && !!a.href,
        iconKey: a.icon_name,
        logo: a.logo || null,
        dot: a.dot_color,
      };
    }),
    () => posts.map(p => {
      const { title, body } = localizedPost(p, locale);
      return {
        kind: 'post',
        id: p.id,
        title: title || t('splash.news.untitled'),
        subtitle: (body || '').replace(/\s+/g, ' ').slice(0, 110),
        route: '/',  // post lives on the splash; future · dedicated /news/:id
        anchor: `post-${p.id}`,
      };
    }),
    () => employees.filter(e => e.is_active !== false).map(e => ({
      kind: 'person',
      id: e.id,
      title: e.full_name || e.email,
      subtitle: [e.poste, e.email].filter(Boolean).join(' · '),
      route: '/#team',
    })),
    () => staticPages(t, isAdmin),
  ], [apps, posts, employees, locale, t, isAdmin]);

  const results = useMemo(() => searchAll(q, sources), [q, sources]);
  const grouped = useMemo(() => groupByKind(results), [results]);

  // Flatten for keyboard navigation across groups.
  const flat = useMemo(
    () => [...grouped.values()].flat(),
    [grouped],
  );

  useEffect(() => {
    if (activeIdx >= flat.length) setActive(0);
  }, [flat.length, activeIdx]);

  if (!open) return null;

  const choose = (row) => {
    if (!row) return;
    if (row.external) {
      window.open(row.route, '_blank', 'noopener');
      onClose();
      return;
    }
    // Some rows put the hash in `route` (e.g. '/#team'), others put it
    // in `anchor`. Normalise to a single target so we can also scroll
    // ourselves · React Router's navigate() only mutates the URL when
    // the path is unchanged, so a same-page hash never triggers the
    // browser's native jump-to-anchor behaviour.
    const target = row.route + (row.anchor ? `#${row.anchor}` : '');
    navigate(target);
    const hashIdx = target.indexOf('#');
    const hash = hashIdx >= 0 ? target.slice(hashIdx + 1) : '';
    if (hash) {
      // Defer until the navigation has settled so the target element
      // exists in the DOM even when we're switching routes.
      requestAnimationFrame(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    onClose();
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(i => Math.min(flat.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(flat[activeIdx]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4"
      style={{ background: 'rgba(20, 30, 25, 0.4)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[640px] rounded-2xl overflow-hidden flex flex-col"
        style={{
          background: LT.beigeLight,
          border: `1px solid ${LT.green30}`,
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          maxHeight: '70vh',
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b"
             style={{ borderColor: LT.green30 }}>
          <Search className="h-4 w-4 shrink-0" style={{ color: LT.green50 }} />
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={e => { setQ(e.target.value); setActive(0); }}
            onKeyDown={onKey}
            placeholder={t('search.placeholder')}
            className="flex-1 bg-transparent outline-none text-[15px]"
            style={{ color: LT.greenDeep }}
          />
          <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] rounded font-num"
               style={{ color: LT.green50, border: `1px solid ${LT.green30}` }}>
            esc
          </kbd>
          <button type="button" onClick={onClose} className="sm:hidden p-1"
                  aria-label={t('common.close') || 'Close'}>
            <X className="h-4 w-4" style={{ color: LT.green50 }} />
          </button>
        </div>

        <div className="overflow-y-auto py-2">
          {!q && (
            <div className="px-4 py-6 text-[13px] text-center" style={{ color: LT.green50 }}>
              {t('search.hint')}
            </div>
          )}
          {q && results.length === 0 && (
            <div className="px-4 py-6 text-[13px] text-center" style={{ color: LT.green50 }}>
              {t('search.noResults')}
            </div>
          )}
          {q && results.length > 0 && (
            <ul className="text-[13.5px]">
              {[...grouped.entries()].map(([kind, rows]) => (
                <li key={kind}>
                  <div className="px-4 pt-3 pb-1 text-[10.5px] uppercase tracking-[0.14em] font-medium"
                       style={{ color: LT.green50 }}>
                    {t(KIND_LABEL[kind] || kind)}
                  </div>
                  <ul>
                    {rows.map((row) => {
                      const flatIdx = flat.indexOf(row);
                      const active = flatIdx === activeIdx;
                      return (
                        <ResultRow
                          key={`${row.kind}-${row.id}`}
                          row={row}
                          active={active}
                          onMouseEnter={() => setActive(flatIdx)}
                          onClick={() => choose(row)}
                        />
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-4 py-2 border-t text-[10.5px] flex items-center gap-3"
             style={{ borderColor: LT.green30, color: LT.green50 }}>
          <span><kbd className="font-num">↑↓</kbd> {t('search.kb.navigate')}</span>
          <span><kbd className="font-num">↵</kbd> {t('search.kb.open')}</span>
          <span className="ml-auto"><kbd className="font-num">esc</kbd> {t('search.kb.close')}</span>
        </div>
      </div>
    </div>
  );
}

const ResultRow = ({ row, active, onMouseEnter, onClick }) => {
  const Icon = row.kind === 'app'
    ? iconFor(row.iconKey)
    : (STATIC_ICONS[row.icon] || {
        post:   Newspaper,
        person: Users,
        page:   FileText,
      }[row.kind] || ArrowRight);
  return (
    <li
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-2 cursor-pointer"
      style={{
        background: active ? `${LT.greenDeep}10` : 'transparent',
      }}
    >
      <span className="h-7 w-7 rounded-md flex items-center justify-center shrink-0 overflow-hidden"
            style={{
              background: LT.beigeLight,
              border: `1px solid ${LT.green30}`,
            }}>
        {row.logo
          ? <img src={row.logo} alt="" className="h-full w-full object-contain p-0.5" />
          : <Icon className="h-3.5 w-3.5" style={{ color: row.dot || LT.green50 }} />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate" style={{ color: LT.greenDeep }}>{row.title}</div>
        {row.subtitle && (
          <div className="text-[11.5px] truncate" style={{ color: LT.green50 }}>{row.subtitle}</div>
        )}
      </div>
      <ArrowRight className="h-3.5 w-3.5 shrink-0" style={{ color: active ? LT.greenDeep : LT.green30 }} />
    </li>
  );
};
